import { useState, useEffect } from 'react';
import { BrowserProvider } from 'ethers';

const API_BASE = import.meta.env.VITE_SHIELD_API_URL || '';

interface Mission {
  id: string;
  title: string;
  description: string;
  tweetUrl?: string;
  tweetId?: string;
  targetAccount?: string;
  actions: string[];
  points: { like: number; follow: number; comment: number; share: number };
  maxCommentBonus: number;
  startsAt?: number;
  endsAt?: number;
  active?: number;
}

interface AdminPanelProps {
  provider?: BrowserProvider | null;
}

export function AdminPanel({ provider }: AdminPanelProps) {
  const [adminKey, setAdminKey] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tweetUrl: '',
    targetAccount: '',
    actions: [] as string[],
    pointsLike: 5,
    pointsFollow: 10,
    pointsComment: 20,
    pointsShare: 15,
    maxCommentBonus: 30,
    startsAt: '',
    endsAt: '',
  });

  // Load admin key from sessionStorage
  useEffect(() => {
    const stored = sessionStorage.getItem('shield_admin_key');
    if (stored) {
      setAdminKey(stored);
      setIsAuthenticated(true);
    }
  }, []);

  const handleAuthenticateAdmin = () => {
    if (adminKey.trim()) {
      sessionStorage.setItem('shield_admin_key', adminKey);
      setIsAuthenticated(true);
      setError('');
      fetchMissions();
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('shield_admin_key');
    setIsAuthenticated(false);
    setAdminKey('');
    setMissions([]);
  };

  const fetchMissions = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/missions`, {
        headers: { Authorization: `Bearer ${adminKey}` },
      });

      if (!res.ok) throw new Error('Failed to fetch missions');
      const data = await res.json() as { missions: Mission[] };
      setMissions(data.missions);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch missions');
    } finally {
      setLoading(false);
    }
  };

  const extractTweetId = (url: string): string => {
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?(?:x|twitter)\.com\/\w+\/status\/(\d+)/);
    return match ? match[1] : '';
  };

  const handleCreateMission = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const tweetId = formData.tweetUrl ? extractTweetId(formData.tweetUrl) : undefined;

      const res = await fetch(`${API_BASE}/api/admin/missions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminKey}`,
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          tweetUrl: formData.tweetUrl || undefined,
          tweetId,
          targetAccount: formData.targetAccount || undefined,
          actions: formData.actions,
          points: {
            like: formData.pointsLike,
            follow: formData.pointsFollow,
            comment: formData.pointsComment,
            share: formData.pointsShare,
          },
          maxCommentBonus: formData.maxCommentBonus,
          startsAt: formData.startsAt ? Math.floor(new Date(formData.startsAt).getTime() / 1000) : undefined,
          endsAt: formData.endsAt ? Math.floor(new Date(formData.endsAt).getTime() / 1000) : undefined,
        }),
      });

      if (!res.ok) throw new Error('Failed to create mission');

      setSuccess('Mission created successfully!');
      setShowCreateForm(false);
      resetForm();
      await fetchMissions();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create mission');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMission = async (missionId: string) => {
    if (!confirm('Are you sure you want to deactivate this mission?')) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/api/admin/missions/${missionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminKey}` },
      });

      if (!res.ok) throw new Error('Failed to delete mission');

      setSuccess('Mission deactivated!');
      await fetchMissions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete mission');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      tweetUrl: '',
      targetAccount: '',
      actions: [],
      pointsLike: 5,
      pointsFollow: 10,
      pointsComment: 20,
      pointsShare: 15,
      maxCommentBonus: 30,
      startsAt: '',
      endsAt: '',
    });
  };

  const toggleAction = (action: string) => {
    setFormData({
      ...formData,
      actions: formData.actions.includes(action)
        ? formData.actions.filter((a) => a !== action)
        : [...formData.actions, action],
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md w-full">
          <h1 className="font-display text-2xl font-semibold mb-6">Admin Panel</h1>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Enter admin key..."
            className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-accent/40 mb-4 font-body"
            onKeyPress={(e) => e.key === 'Enter' && handleAuthenticateAdmin()}
          />
          <button
            onClick={handleAuthenticateAdmin}
            className="w-full px-4 py-2 rounded-ternoa bg-accent text-white font-semibold hover:bg-accent-hover transition-colors cursor-pointer border-none font-body"
          >
            Authenticate
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-semibold">Admin Panel</h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 rounded-ternoa bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-colors cursor-pointer border-none font-body"
          >
            Logout
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-ternoa bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 rounded-ternoa bg-success/10 border border-success/30 text-success text-sm">
            {success}
          </div>
        )}

        <div className="mb-8">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-6 py-2 rounded-ternoa bg-accent text-white font-semibold hover:bg-accent-hover transition-colors cursor-pointer border-none font-body"
          >
            {showCreateForm ? 'Cancel' : '+ New Mission'}
          </button>
        </div>

        {showCreateForm && (
          <form onSubmit={handleCreateMission} className="glass-card p-8 mb-8">
            <h2 className="font-display text-xl font-semibold mb-6">Create Mission</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Target Account (optional)</label>
                <input
                  type="text"
                  value={formData.targetAccount}
                  onChange={(e) => setFormData({ ...formData, targetAccount: e.target.value })}
                  placeholder="@handle"
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-1">Tweet URL (optional)</label>
              <input
                type="text"
                value={formData.tweetUrl}
                onChange={(e) => setFormData({ ...formData, tweetUrl: e.target.value })}
                placeholder="https://x.com/user/status/123..."
                className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 placeholder:text-gray-600 focus:outline-none focus:border-accent/40 font-body"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-3">Actions</label>
              <div className="flex flex-wrap gap-2">
                {['like', 'follow', 'comment', 'share'].map((action) => (
                  <label key={action} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.actions.includes(action)}
                      onChange={() => toggleAction(action)}
                      className="w-4 h-4 rounded accent-accent"
                    />
                    <span className="text-sm text-gray-300 capitalize">{action}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Like Points</label>
                <input
                  type="number"
                  value={formData.pointsLike}
                  onChange={(e) => setFormData({ ...formData, pointsLike: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Follow Points</label>
                <input
                  type="number"
                  value={formData.pointsFollow}
                  onChange={(e) => setFormData({ ...formData, pointsFollow: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Comment Points</label>
                <input
                  type="number"
                  value={formData.pointsComment}
                  onChange={(e) => setFormData({ ...formData, pointsComment: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Share Points</label>
                <input
                  type="number"
                  value={formData.pointsShare}
                  onChange={(e) => setFormData({ ...formData, pointsShare: parseInt(e.target.value) })}
                  min="0"
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-1">Max Comment Bonus</label>
              <input
                type="number"
                value={formData.maxCommentBonus}
                onChange={(e) => setFormData({ ...formData, maxCommentBonus: parseInt(e.target.value) })}
                min="0"
                className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Starts At (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.startsAt}
                  onChange={(e) => setFormData({ ...formData, startsAt: e.target.value })}
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Ends At (optional)</label>
                <input
                  type="datetime-local"
                  value={formData.endsAt}
                  onChange={(e) => setFormData({ ...formData, endsAt: e.target.value })}
                  className="w-full px-4 py-2 rounded-ternoa bg-surface-raised border border-border-subtle text-gray-300 focus:outline-none focus:border-accent/40 font-body"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.title || !formData.description}
              className="w-full px-6 py-2 rounded-ternoa bg-accent text-white font-semibold hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer border-none font-body"
            >
              {loading ? 'Creating...' : 'Create Mission'}
            </button>
          </form>
        )}

        <div className="space-y-4">
          <h2 className="font-display text-xl font-semibold mb-4">Active Missions</h2>

          {loading && !missions.length ? (
            <p className="text-gray-400 text-center py-8">Loading missions...</p>
          ) : missions.length === 0 ? (
            <p className="text-gray-400 text-center py-8">No missions yet</p>
          ) : (
            missions.map((mission) => (
              <div key={mission.id} className="glass-card p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-display text-lg font-semibold">{mission.title}</h3>
                    <p className="text-sm text-gray-400 mt-1">{mission.description}</p>
                  </div>
                  {!mission.active && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400">
                      Inactive
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-4">
                  {mission.actions.map((action) => (
                    <div key={action} className="text-gray-400">
                      <span className="capitalize">{action}:</span>
                      <span className="font-semibold text-accent ml-1">
                        +{mission.points[action as keyof typeof mission.points] || 0}
                      </span>
                    </div>
                  ))}
                </div>

                {mission.targetAccount && (
                  <p className="text-xs text-gray-500 mb-3">Target: @{mission.targetAccount}</p>
                )}

                {mission.tweetUrl && (
                  <a
                    href={mission.tweetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent hover:text-accent-hover underline mb-3 block"
                  >
                    View tweet
                  </a>
                )}

                {mission.active && (
                  <button
                    onClick={() => handleDeleteMission(mission.id)}
                    className="mt-4 px-4 py-2 rounded-ternoa bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium hover:bg-red-500/20 transition-colors cursor-pointer"
                  >
                    Deactivate
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
